import { useState } from 'react'
import { PageHeader } from '@/widgets/layout/ui/AppLayout'
import { useAdminBalance } from '@/entities/admin'
import type {
  AdminBalanceDays,
  AdminBalanceProvider,
  AdminBalanceResponse,
} from '@/shared/api/types'
import { AdminRemoteState } from '../shared/AdminRemoteState'
import { ElevenLabsCost } from '../shared/ElevenLabsCost'
import { formatAdminDate, formatNumber } from '../shared/format'
import { formatUsd } from '../shared/adminCosts'
import { BalanceHistoryCharts, DailySpendChart } from './BalanceCharts'
import {
  balanceAlert,
  balanceStatusTone,
  isCompleteBalanceResponse,
  providerDisplayName,
  providerRemainingRatio,
  providerSnapshotAvailable,
  providerStatusLabel,
} from './balanceModel'
import common from '../AdminPages.module.css'
import styles from './AdminBalancePage.module.css'

const PERIODS: { value: AdminBalanceDays; label: string }[] = [
  { value: 7, label: '7 дней' },
  { value: 30, label: '30 дней' },
  { value: 90, label: '90 дней' },
]

const EXPECTED_PROVIDERS = ['openrouter', 'elevenlabs'] as const

export function AdminBalancePage() {
  const [days, setDays] = useState<AdminBalanceDays>(30)
  const balance = useAdminBalance(days)

  return (
    <>
      <div className={common.headerRow}>
        <PageHeader
          title="Баланс"
          description="Остатки, расход и прогноз по OpenRouter и ElevenLabs."
        />
        <div className={styles.periods} aria-label="Период расходов">
          {PERIODS.map((period) => (
            <button
              key={period.value}
              type="button"
              className={`${styles.periodButton} ${days === period.value ? styles.periodActive : ''}`}
              aria-pressed={days === period.value}
              onClick={() => setDays(period.value)}
            >
              {period.label}
            </button>
          ))}
        </div>
      </div>

      {balance.isPending ? (
        <AdminRemoteState kind="loading" />
      ) : balance.isError || !balance.data ? (
        <AdminRemoteState
          kind="error"
          title="Баланс не загрузился"
          message="Провайдеры или сервер недоступны. Повторите запрос — старые значения не подставляются."
          onRetry={() => void balance.refetch()}
        />
      ) : !isCompleteBalanceResponse(balance.data) ? (
        <AdminRemoteState
          kind="error"
          title="Сервер вернул неполные данные"
          message="В ответе нет обязательных данных провайдеров или тарифа ElevenLabs."
          onRetry={() => void balance.refetch()}
        />
      ) : balance.data.providers.length === 0 ? (
        <AdminRemoteState
          kind="empty"
          title="Данных о балансе пока нет"
          message="После первого опроса провайдеров здесь появятся остатки и история расходов."
        />
      ) : (
        <BalanceContent data={balance.data} />
      )}
    </>
  )
}

function BalanceContent({ data }: { data: AdminBalanceResponse }) {
  const byName = new Map(data.providers.map((provider) => [provider.provider.toLowerCase(), provider]))
  const availableProviders = data.providers.filter(providerSnapshotAvailable)
  const unexpected = data.providers.filter(
    (provider) => !EXPECTED_PROVIDERS.includes(provider.provider.toLowerCase() as (typeof EXPECTED_PROVIDERS)[number]),
  )

  return (
    <>
      <section className={styles.providerGrid} aria-label="Остатки провайдеров">
        {EXPECTED_PROVIDERS.map((provider) => {
          const value = byName.get(provider)
          return value ? (
            <ProviderCard
              key={provider}
              provider={value}
              days={data.days}
              rate={data.elevenLabsUsdPer1kCredits}
            />
          ) : (
            <UnavailableProviderCard
              key={provider}
              provider={provider}
              message="Провайдер отсутствует в обязательном ответе API."
            />
          )
        })}
        {unexpected.map((provider) => (
          <ProviderCard
            key={provider.provider}
            provider={provider}
            days={data.days}
            rate={data.elevenLabsUsdPer1kCredits}
          />
        ))}
      </section>

      <BalanceHistoryCharts providers={availableProviders} />
      <DailySpendChart providers={availableProviders} />
    </>
  )
}

function ProviderCard({
  provider,
  days,
  rate,
}: {
  provider: AdminBalanceProvider
  days: AdminBalanceDays
  rate: number
}) {
  if (!providerSnapshotAvailable(provider)) {
    return (
      <UnavailableProviderCard
        provider={provider.provider}
        message="Провайдер недоступен — актуальный снимок не получен."
      />
    )
  }

  const tone = balanceStatusTone(provider)
  const alert = balanceAlert(provider)
  const ratio = providerRemainingRatio(provider)
  const normalizedName = provider.provider.toLowerCase()
  const isElevenLabs = normalizedName === 'elevenlabs'
  const toneClass = {
    good: styles.statusGood,
    danger: styles.statusDanger,
    stale: styles.statusStale,
    error: styles.statusError,
  }[tone]

  return (
    <article className={`${styles.providerCard} ${tone === 'danger' ? styles.providerDanger : ''}`}>
      <div className={styles.providerHeader}>
        <div>
          <p className={styles.providerEyebrow}>Провайдер</p>
          <h2 className={styles.providerName}>{providerDisplayName(provider.provider)}</h2>
        </div>
        <span className={`${styles.statusBadge} ${toneClass}`}>{providerStatusLabel(provider)}</span>
      </div>

      <strong className={styles.balanceValue} data-admin-value>
        {isElevenLabs ? (
          <ElevenLabsCost
            credits={provider.balance}
            usd={provider.balanceUsd}
            rate={rate}
            unit="long"
          />
        ) : Number.isFinite(provider.balance) ? (
          `${formatUsd(provider.balance)} осталось`
        ) : (
          '$— осталось'
        )}
      </strong>

      {ratio !== null && (
        <div
          className={styles.balanceProgress}
          role="progressbar"
          aria-label={`Остаток ${providerDisplayName(provider.provider)}`}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.max(0, Math.min(100, Math.round(ratio * 100)))}
        >
          <span style={{ width: `${Math.max(0, Math.min(100, ratio * 100))}%` }} />
        </div>
      )}

      {alert ? (
        <p className={styles.providerAlert} role="alert">
          {alert}
        </p>
      ) : tone === 'error' ? (
        <p className={styles.providerAlert} role="alert">
          Провайдер недоступен — показаны последние полученные данные.
        </p>
      ) : provider.stale ? (
        <p className={styles.providerWarning} role="status">
          Данные устарели — проверьте провайдера перед пополнением.
        </p>
      ) : tone === 'danger' ? (
        <p className={styles.providerAlert} role="alert">
          Баланс заканчивается — осталось меньше 10% лимита.
        </p>
      ) : null}

      <dl className={styles.providerFacts}>
        <Fact label={`Расход за ${days} дней`} value={formatOptionalUsd(provider.spendUsd)} />
        <Fact label="Использовано" value={formatProviderUsage(provider)} />
        <Fact label="Прогноз" value={forecastLabel(provider.forecastDays)} />
        <Fact label="Следующий сброс" value={formatAdminDate(provider.nextReset)} />
        <Fact label="Обновлено" value={formatAdminDate(provider.lastUpdated)} />
      </dl>
    </article>
  )
}

function UnavailableProviderCard({ provider, message }: { provider: string; message: string }) {
  return (
    <article className={`${styles.providerCard} ${styles.providerMissing}`} role="alert">
      <div className={styles.providerHeader}>
        <div>
          <p className={styles.providerEyebrow}>Провайдер</p>
          <h2 className={styles.providerName}>{providerDisplayName(provider)}</h2>
        </div>
        <span className={`${styles.statusBadge} ${styles.statusError}`}>Нет данных</span>
      </div>
      <strong className={styles.balanceValue} data-admin-value>
        Нет данных
      </strong>
      <p className={styles.providerAlert}>{message}</p>
    </article>
  )
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.factRow}>
      <dt>{label}</dt>
      <dd data-admin-value>{value}</dd>
    </div>
  )
}

function forecastLabel(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return 'Недоступен'
  if (value < 0) return 'Баланс исчерпан'
  return `≈ ${formatNumber(Math.round(value))} дн.`
}

function formatOptionalUsd(value: number): string {
  return Number.isFinite(value) ? formatUsd(value) : '$—'
}

function formatProviderUsage(provider: AdminBalanceProvider): string {
  if (!Number.isFinite(provider.used) || !Number.isFinite(provider.limit)) return 'Нет данных'
  if (provider.provider.toLowerCase() === 'elevenlabs') {
    return `${formatNumber(provider.used)} из ${formatNumber(provider.limit)} кр.`
  }
  return `${formatUsd(provider.used)} из ${formatUsd(provider.limit)}`
}
